import { Rect } from "./size";
import { SVG } from "./svg";

export interface GraphValue {
  amount: number;
  label: string;
}

export interface GraphTotals {
  max: number;
  totals: number[];
}

export interface GraphScale {
  min: number;
  max: number;
  pixel_length: number;
}

export class Graph {

  static totals(data: GraphValue[][]): GraphTotals {
    var totals: number[] = [];
    for (let entries of data) {
      var total = 0;
      for (let entry of entries) {
        total += entry.amount;
      }
      totals.push(total);
    }
    let max = 0;
    if (totals.length > 0) {
      max = Math.max.apply(null, totals);
    }
    return {
      max: max,
      totals: totals,
    }
  }

  static formated_number(n: number): string {
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  static compact_number(n: number, min: number = 1e3): string {
    if (n < min) {
      return Graph.formated_number(n);
    }
    if (n < 1e6) {
      return (n / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "K";
    }
    if (n < 1e9) {
      return (n / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "M";
    }
    return (n / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "B";
  }

  static render_title(svg: SVGElement, id: string, title: string, width: number, inset: number) {
    const line_y = inset + 21;
    const title_y = inset + 15;

    const title_el = document.createElementNS("http://www.w3.org/2000/svg", "text");
    title_el.setAttribute("x", inset.toString());
    title_el.setAttribute("y", title_y.toString());
    title_el.style.fontFamily = SVG.font;
    title_el.setAttribute("fill", "#333");
    title_el.style.fontSize = "11px";
    title_el.innerHTML = title.toUpperCase();
    title_el.classList.add("s-title");
    title_el.id = "t_" + id;
    svg.appendChild(title_el);

    for (let i = 0; i < 2; i++) {
      const l1 = document.createElementNS("http://www.w3.org/2000/svg", "line");
      l1.setAttribute("x1", inset.toString());
      l1.setAttribute("x2", (width - inset).toString());
      l1.setAttribute("y1", (line_y + (i * 2)).toString());
      l1.setAttribute("y2", (line_y + (i * 2)).toString());
      l1.setAttribute("stroke", "#808080");
      svg.appendChild(l1);
    }
  }

  static number_scale(val: number): number {
    const r50 = Math.sqrt(50);
    const r10 = Math.sqrt(10);
    const r2 = Math.sqrt(2);
    const power = Math.floor(Math.log(val) / Math.log(10));
    let scale = val / Math.pow(10, power);
    if (scale >= r50) {
      scale = 10;
    } else if (scale >= r10) {
      scale = 5;
    } else if (scale >= r2) {
      scale = 2;
    } else {
      scale = 1;
    }
    return scale;
  }

  static render_y_scale(container: SVGElement, rect: Rect, max: number): GraphScale {
    const scale_vals: number[] = [];
    const scale_labels: string[] = [];

    const pad = 5;
    const count = 3;
    const min = 0;
    const div = (max - min) / count;
    const power = Math.floor(Math.log(div) / Math.log(10));
    let step = Graph.number_scale(div);
    if (power >= 0) {
      step = step * Math.pow(10, power);
    } else {
      step = 1 / (Math.pow(10, -power) / step);
    }

    const text_el = function (label: string): SVGTextElement {
      const t = document.createElementNS("http://www.w3.org/2000/svg", "text");
      t.style.fontSize = "10px";
      t.style.fontFamily = SVG.font;
      t.setAttribute("fill", "#cccccc");
      t.innerHTML = label;
      return t;
    }

    let y = 0;
    let w = 0;
    while (y < (max + step)) {
      scale_vals.push(y);
      const l = Graph.compact_number(y);
      scale_labels.push(l);
      const t = text_el(l);
      container.appendChild(t);
      const width = t.getComputedTextLength();
      container.removeChild(t);
      w = Math.max(w, width);
      y += step;
    }
    const adjusted_max = scale_vals[scale_vals.length - 1];

    let idx = 0;
    for (const v of scale_vals) {
      const y = rect.height - (v / adjusted_max) * rect.height;
      const l = document.createElementNS("http://www.w3.org/2000/svg", "line");
      l.setAttribute("x1", rect.left.toString());
      l.setAttribute("x2", (rect.left + rect.width).toString());
      l.setAttribute("y1", (rect.top + y).toString());
      l.setAttribute("y2", (rect.top + y).toString());
      l.setAttribute("stroke", "#efefef");
      container.appendChild(l);

      const t = text_el(scale_labels[idx]);
      t.setAttribute("x", (rect.left + rect.width - w).toString());
      t.setAttribute("y", (rect.top + y - 3).toString());
      t.innerHTML = scale_labels[idx];
      container.appendChild(t);
      idx++;
    }

    return {
      min: 0,
      max: adjusted_max,
      pixel_length: w + pad,
    }
  }
}